import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption {
  id: number;
  label: string;
}

const DROPDOWN_HEIGHT_ESTIMATE_PX = 260;
const DROPDOWN_GAP_PX = 4;

interface DropdownPosition {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value?: number;
  onChange: (id: number | undefined) => void;
  onSearch?: (query: string) => void;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  placeholder?: string;
  allLabel?: string;
  required?: boolean;
  className?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  onSearch,
  onLoadMore,
  loadingMore = false,
  placeholder = 'Selecionar...',
  allLabel = 'Todos',
  required = false,
  className = '',
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const listboxId = useId();

  const selected = options.find((o) => o.id === value);

  const filtered = onSearch
    ? options
    : query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
      : options;

  useEffect(() => {
    if (!onSearch) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, onSearch]);

  const handleListScroll = () => {
    const el = listRef.current;
    if (!el || !onLoadMore || loadingMore) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      onLoadMore();
    }
  };

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const trigger = containerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const shouldFlipUp = spaceBelow < DROPDOWN_HEIGHT_ESTIMATE_PX && rect.top > spaceBelow;

      setPosition(
        shouldFlipUp
          ? { left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top + DROPDOWN_GAP_PX }
          : { left: rect.left, width: rect.width, top: rect.bottom + DROPDOWN_GAP_PX }
      );
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const handleSelect = (id: number | undefined) => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`} onKeyDown={handleKeyDown}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={[
          'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left',
          open || value
            ? 'border-chocolate text-text-dark'
            : 'border-champagne-dark text-text-medium hover:border-chocolate/60',
        ].join(' ')}
      >
        <span className={selected ? 'text-text-dark' : 'text-text-light'}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {!required && value !== undefined && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); handleSelect(undefined); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSelect(undefined)}
              className="text-text-light hover:text-chocolate cursor-pointer p-0.5 rounded transition-colors"
              aria-label="Limpar seleção"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg
            className={`w-4 h-4 text-text-light transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Dropdown — portaled to <body> so it can't be clipped by a scrollable/overflow-hidden ancestor (e.g. a table wrapper) */}
      {open && position && createPortal(
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          style={{
            position: 'fixed',
            left: position.left,
            width: position.width,
            top: position.top,
            bottom: position.bottom,
          }}
          className="z-50 bg-white border border-champagne-dark rounded-xl shadow-lg overflow-hidden"
        >
          {/* Search */}
          <div className="p-2 border-b border-champagne">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar..."
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-champagne-dark focus:outline-none focus:border-chocolate bg-champagne-light"
              />
            </div>
          </div>

          {/* Options */}
          <ul ref={listRef} onScroll={handleListScroll} className="max-h-48 overflow-y-auto py-1">
            {!required && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === undefined}
                  onClick={() => handleSelect(undefined)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                    value === undefined
                      ? 'bg-champagne text-chocolate font-medium'
                      : 'text-text-medium hover:bg-champagne-light'
                  }`}
                >
                  {value === undefined && (
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span className={value === undefined ? '' : 'pl-5'}>{allLabel}</span>
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-text-light text-center">Nenhum resultado</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={value === opt.id}
                    onClick={() => handleSelect(opt.id)}
                    className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                      value === opt.id
                        ? 'bg-champagne text-chocolate font-medium'
                        : 'text-text-medium hover:bg-champagne-light'
                    }`}
                  >
                    {value === opt.id && (
                      <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className={value === opt.id ? '' : 'pl-5'}>{opt.label}</span>
                  </button>
                </li>
              ))
            )}
            {loadingMore && (
              <li className="px-3 py-2 text-xs text-text-light text-center">Carregando...</li>
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
